import React, {
  Children,
  isValidElement,
  ReactNode,
  useEffect,
  useState
} from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism'
import { FiCheck, FiCode, FiCopy } from 'react-icons/fi'
import heading from './Heading'
import AComponent from './a'
import input from './components/input'
import { Image, ul } from './styles'
import { getMarkdownImageProps } from '@/lib/markdownImageProps'

type MarkdownComponentsOptions = {
  baseUrl?: string | null
  rootUrl?: string | null
  projectSlug?: string
  currentReadmePath?: string | null
}

const isAbsoluteUrl = (src: string) =>
  /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(src) ||
  src.startsWith('data:') ||
  src.startsWith('blob:') ||
  src.startsWith('mailto:')

const BADGE_HOSTS = ['img.shields.io', 'badgen.net', 'badge.fury.io']

const isWhitespaceNode = (node: unknown) =>
  typeof node === 'string' && node.trim().length === 0

const isBrNode = (node: unknown) => isValidElement(node) && node.type === 'br'

const isBadgeImageNode = (node: unknown) => {
  if (!isValidElement(node) || node.type !== Image) return false

  const src = typeof node.props?.src === 'string' ? node.props.src : ''
  if (!src) return false

  return BADGE_HOSTS.some(host => src.includes(host))
}

const isBadgeLinkNode = (node: unknown) => {
  if (!isValidElement(node) || node.type !== AComponent) return false

  const linkChildren = Children.toArray(node.props?.children).filter(
    child => !isWhitespaceNode(child)
  )

  if (linkChildren.length === 0) return false

  return linkChildren.every(isBadgeImageNode)
}

const LANGUAGE_LABELS: Record<string, string> = {
  ts: 'TypeScript',
  tsx: 'TypeScript',
  js: 'JavaScript',
  jsx: 'JavaScript',
  json: 'JSON',
  css: 'CSS',
  scss: 'SCSS',
  html: 'HTML',
  md: 'Markdown',
  yml: 'YAML',
  yaml: 'YAML',
  sh: 'Shell',
  bash: 'Shell',
  zsh: 'Shell'
}

const normalizeLanguageLabel = (language: string) => {
  const normalized = language.toLowerCase()
  if (!normalized) return 'Code'

  return (
    LANGUAGE_LABELS[normalized] ||
    normalized.charAt(0).toUpperCase() + normalized.slice(1)
  )
}

const getNodeText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join('')
  }

  if (isValidElement(node)) {
    return getNodeText(node.props?.children)
  }

  return ''
}

const copyToClipboard = async (value: string): Promise<boolean> => {
  if (!value) return false

  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      navigator.clipboard.writeText
    ) {
      await navigator.clipboard.writeText(value)
      return true
    }

    if (typeof document === 'undefined') return false

    const textArea = document.createElement('textarea')
    textArea.value = value
    textArea.setAttribute('readonly', 'readonly')
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    textArea.style.pointerEvents = 'none'
    textArea.style.left = '-9999px'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)

    return copied
  } catch {
    return false
  }
}

type PreProps = {
  children?: ReactNode
}

const Pre = ({ children }: PreProps) => {
  const [copied, setCopied] = useState(false)

  const codeElement = Children.toArray(children).find(isValidElement) as
    | React.ReactElement<{ className?: string; children?: ReactNode }>
    | undefined

  const className =
    typeof codeElement?.props?.className === 'string'
      ? codeElement.props.className
      : ''
  const languageMatch = className.match(/language-([\w-]+)/)
  const languageLabel = normalizeLanguageLabel(languageMatch?.[1] || '')
  const code = getNodeText(codeElement?.props?.children ?? children).replace(
    /\n$/,
    ''
  )

  useEffect(() => {
    if (!copied) return

    const timeout = window.setTimeout(() => {
      setCopied(false)
    }, 1400)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [copied])

  const handleCopy = async () => {
    const didCopy = await copyToClipboard(code)
    if (didCopy) {
      setCopied(true)
    }
  }

  return (
    <div className="code-block-shell">
      <div className="code-block-toolbar">
        <span className="code-lang">
          <FiCode />
          {languageLabel}
        </span>
        <button
          type="button"
          className="code-copy-btn"
          onClick={handleCopy}
          disabled={!code}
          aria-label={copied ? 'Code copied' : 'Copy code'}
          title={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? <FiCheck /> : <FiCopy />}
        </button>
      </div>
      <pre>{children}</pre>
    </div>
  )
}

type CodeProps = {
  children?: ReactNode
  className?: string
  parentName?: string
}

const Code = ({ children, className = '', parentName }: CodeProps) => {
  const code = String(children || '')
  const matchLanguage = className.match(/language-([\w-]+)/)
  const blockLanguage = matchLanguage?.[1] || ''
  const isBlock =
    parentName === 'pre' || Boolean(blockLanguage) || code.includes('\n')

  if (!isBlock) {
    return <code className={className}>{children}</code>
  }

  const language = blockLanguage || 'typescript'
  const normalizedCode = code.replace(/\n$/, '')

  return (
    <SyntaxHighlighter
      language={language}
      style={oneDark}
      PreTag="span"
      CodeTag="code"
      customStyle={{
        margin: 0,
        padding: 0,
        borderRadius: 0,
        border: '0',
        background: 'transparent',
        boxShadow: 'none',
        overflowX: 'visible'
      }}
      codeTagProps={{
        className,
        style: {
          fontFamily:
            "'JetBrains Mono', 'Fira Code', 'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', monospace",
          fontSize: '0.95rem',
          lineHeight: 1.65
        }
      }}
    >
      {normalizedCode}
    </SyntaxHighlighter>
  )
}

const resolveImageSrc = (
  src?: string,
  baseUrl?: string | null,
  rootUrl?: string | null
) => {
  if (!src || typeof src !== 'string') return src

  const trimmed = src.trim()
  if (!trimmed || trimmed.startsWith('#') || isAbsoluteUrl(trimmed)) {
    return trimmed
  }

  if (trimmed.startsWith('/')) {
    if (rootUrl) {
      const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`
      return `${normalizedRoot}${trimmed.slice(1)}`
    }

    return trimmed
  }

  if (baseUrl) {
    try {
      return new URL(trimmed, baseUrl).toString()
    } catch (error) {
      return trimmed
    }
  }

  return trimmed
}

const splitByHash = (value: string): [string, string] => {
  const hashIndex = value.indexOf('#')
  if (hashIndex === -1) return [value, '']
  return [value.slice(0, hashIndex), value.slice(hashIndex)]
}

const splitByQuery = (value: string): [string, string] => {
  const queryIndex = value.indexOf('?')
  if (queryIndex === -1) return [value, '']
  return [value.slice(0, queryIndex), value.slice(queryIndex)]
}

const normalizePath = (value: string): string => {
  const stack: string[] = []
  value
    .replace(/\\/g, '/')
    .split('/')
    .forEach(part => {
      if (!part || part === '.') return
      if (part === '..') {
        stack.pop()
        return
      }
      stack.push(part)
    })

  return stack.join('/')
}

const dirname = (value: string): string => {
  const normalized = normalizePath(value)
  if (!normalized) return ''
  const parts = normalized.split('/')
  parts.pop()
  return parts.join('/')
}

const isMarkdownPath = (value: string): boolean => /\.(md|mdx)$/i.test(value)

const resolveProjectDocHref = (
  href?: string,
  projectSlug?: string,
  currentReadmePath?: string | null
): string | undefined => {
  if (!href || typeof href !== 'string') return href
  if (!projectSlug) return href

  const trimmed = href.trim()
  if (!trimmed || trimmed.startsWith('#') || isAbsoluteUrl(trimmed)) {
    return trimmed
  }

  const [withoutHash, hash] = splitByHash(trimmed)
  const [pathPart, query] = splitByQuery(withoutHash)
  if (!isMarkdownPath(pathPart)) {
    return trimmed
  }

  const baseDir = dirname(currentReadmePath || 'README.md')
  const resolvedPath = pathPart.startsWith('/')
    ? normalizePath(pathPart)
    : normalizePath(`${baseDir}/${pathPart}`)

  if (!resolvedPath) return trimmed

  return `/projects/${projectSlug}/${resolvedPath}${query}${hash}`
}

function Opa(children) {
  return <h1>{children}</h1>
}

const Paragraph = ({ children }) => {
  const nodes = Children.toArray(children)
  const meaningfulNodes = nodes.filter(
    node => !isWhitespaceNode(node) && !isBrNode(node)
  )
  const isBadgeRow =
    meaningfulNodes.length > 0 && meaningfulNodes.every(isBadgeLinkNode)

  if (isBadgeRow) {
    return (
      <div className="badge-row">
        {meaningfulNodes.map((node, index) =>
          isValidElement(node) ? React.cloneElement(node, { key: index }) : null
        )}
      </div>
    )
  }

  return <p>{children}</p>
}

const baseComponents = {
  heading,
  ul,
  a: AComponent,
  input,
  pre: Pre,
  code: Code,
  Opa,
  p: Paragraph
}

const createMarkdownComponents = (options: MarkdownComponentsOptions = {}) => ({
  ...baseComponents,
  a: props => {
    const resolvedHref = resolveProjectDocHref(
      props?.href,
      options.projectSlug,
      options.currentReadmePath
    )

    return <AComponent {...props} href={resolvedHref || props?.href} />
  },
  img: props => {
    const resolvedSrc = resolveImageSrc(
      props?.src,
      options.baseUrl,
      options.rootUrl
    )

    return (
      <Image
        {...props}
        {...getMarkdownImageProps(props)}
        src={resolvedSrc}
      />
    )
  }
})

export const markdownComponents = createMarkdownComponents()
export default createMarkdownComponents
